import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';

import { AuthService } from '../../auth/auth.service';
import { NotificationService } from '../../core/notification.service';
import { toYearOptions } from '../../core/reinsurance-years';
import { ReinsuranceYearsService, STATIC_VALUES_REPORT } from '../../core/reinsurance-years.service';
import { StaticValuesService } from './static-values.service';
import { StaticValueRow, UpdateStaticValueRequest } from './static-values.model';

/**
 * Maintenance screen for the static - input values.
 *
 * Lists the records for a chosen reinsurance year in a table; each row can be
 * edited (Value and Comment only) via a dialog. Saving updates the
 * SummaryReport_StaticValues table and records the change in the audit log
 * (handled server-side in one transaction).
 */
@Component({
  selector: 'app-static-values',
  standalone: false,
  templateUrl: './static-values.component.html',
  styleUrl: './static-values.component.scss'
})
export class StaticValuesComponent implements OnInit {
  rows: StaticValueRow[] = [];
  years: number[] = [];
  selectedYear!: number;

  loading = false;
  saving = false;
  error: string | null = null;

  /** The dialog is open while this holds the row being edited. */
  editDialogVisible = false;
  editingRow: StaticValueRow | null = null;
  editValue: number | null = null;
  editComment = '';

  constructor(
    private readonly service: StaticValuesService,
    private readonly reinsuranceYears: ReinsuranceYearsService,
    private readonly title: Title,
    private readonly notifications: NotificationService,
    private readonly auth: AuthService
  ) {}

  ngOnInit(): void {
    this.title.setTitle('Static - Input Values');
    this.loadYears();
  }

  /**
   * Fills the Reinsurance Year dropdown with the years the table holds,
   * selects the latest, then loads that year's values.
   */
  private loadYears(): void {
    this.loading = true;
    this.reinsuranceYears.getYears(STATIC_VALUES_REPORT).subscribe({
      next: (years) => this.applyYears(years),
      // Without the year list the values can still load: fall back to recent years.
      error: () => this.applyYears([])
    });
  }

  private applyYears(years: number[]): void {
    this.years = toYearOptions(years);
    this.selectedYear = this.years[0];
    this.load();
  }

  /** Loads the static values for the selected reinsurance year. */
  load(): void {
    this.loading = true;
    this.error = null;
    this.service.getStaticValues(this.selectedYear).subscribe({
      next: (rows) => {
        this.rows = rows;
        this.loading = false;
      },
      error: () => {
        this.rows = [];
        this.loading = false;
        this.error = 'Could not load the static values. Please try again.';
      }
    });
  }

  /** Opens the edit dialog seeded with the row's current editable values. */
  openEdit(row: StaticValueRow): void {
    this.editingRow = row;
    this.editValue = row.value;
    this.editComment = row.comment ?? '';
    this.error = null;
    this.editDialogVisible = true;
  }

  /** Closes the edit dialog without saving. */
  cancelEdit(): void {
    this.editDialogVisible = false;
    this.editingRow = null;
  }

  /** Persists the edited Value/Comment and refreshes the grid on success. */
  saveEdit(): void {
    const row = this.editingRow;
    if (!row || row.reinsuranceyear == null) {
      return;
    }

    const request: UpdateStaticValueRequest = {
      measure: row.measure,
      reinsuranceYear: row.reinsuranceyear,
      qtrStatus: row.qtrstatus,
      insurancePlanAbbreviation: row.insuranceplanabbreviation,
      fundDesignationFlag: row.funddesignationflag,
      value: this.editValue,
      comment: this.editComment?.trim() ? this.editComment.trim() : null,
      userId: this.auth.getUsername(),
      loggedOn: toLocalIso(new Date())
    };

    this.saving = true;
    this.error = null;
    this.service.updateStaticValue(request).subscribe({
      next: () => {        
        this.saving = false;
        
        // Close dialog
        this.editDialogVisible = false;
        this.editingRow = null;

        // Reload latest data from database
        this.load();
        
        this.notifications.success('Saved', 'Static value updated and change logged.');
      },
      error: () => {
        this.saving = false;
        this.error = 'Could not save the change. Please try again.';
      }
    });
  }

  /** Formats a numeric value for display (2 decimals, thousands separators). */
  formatValue(value: number | null): string {
    if (value == null) {
      return '';
    }
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 6,
      maximumFractionDigits: 6
    });
  }

  /** Formats an ISO date for display, or returns '' when absent/invalid. */
  formatDate(raw: string | null): string {
    if (!raw) {
      return '';
    }
    const date = new Date(raw);
    return isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US');
  }

  /** Short label describing the record currently being edited. */
  get editingLabel(): string {
    const row = this.editingRow;
    if (!row) {
      return '';
    }
    const parts = [row.measure, row.insuranceplanabbreviation, row.funddesignationflag].filter(
      (p): p is string => !!p
    );
    return parts.join(' · ');
  }
}

/**
 * Formats a date as a naive local ISO string (`YYYY-MM-DDTHH:mm:ss`), i.e. the
 * user's wall-clock time with no time-zone conversion, so the audit LoggedOn
 * reflects the user's own system clock rather than the server's.
 */
function toLocalIso(date: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}
